import React, { ReactNode } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import { logout } from '@dvag/dfs-orchestrator-client/messengers';
import { Error403, Error404, ErrorCrashedApp } from '@dvag/dfs-ui-blocks/components';
import { FallbackError } from './type/FallbackError';
import { getMonitoring } from './service/getMonitoring';
import { isSalesforceWishesAndGoals } from './utils/util';
import { navigateToMicroApp } from '@dvag/dfs-orchestrator-client/navigation';
import { appNames } from '@dvag/dfs-constant-config/app-names';
import { sharedAppRoutes } from '@dvag/dfs-constant-config/shared-app-names';
import { HttpStatusCode } from 'axios';

export type RouterErrorBoundaryProps = {
  children: ReactNode;
};

const navigateToAdvisorDashboard = () => {
  navigateToMicroApp(appNames.advisor, sharedAppRoutes.advisor.householdSearch);
};

export const RouterErrorBoundary = ({ children }: RouterErrorBoundaryProps) => {
  const location = useLocation();
  const monitoring = getMonitoring();

  const isUnauthorized = (error: FallbackError) =>
    error?.response?.status === HttpStatusCode.Unauthorized;

  const isForbidden = (error: FallbackError) =>
    [error?.response?.status, error?.cause].includes(HttpStatusCode.Forbidden);

  const isNotFound = (error: FallbackError) =>
    [error?.response?.status, error.cause, error?.response?.errors?.[0]?.code].includes(
      HttpStatusCode.NotFound,
    );

  const fallbackComponent = ({ error }: FallbackProps) => {
    const isSalesforce = isSalesforceWishesAndGoals(location.pathname);
    const showButton = !isSalesforce;
    const onAction = () => navigateToAdvisorDashboard();
    const showButtonProps = { showActionButton: true, onAction } as const;
    const hideButtonProps = { showActionButton: false } as const;
    const errorPageProps = showButton ? showButtonProps : hideButtonProps;
    if (isForbidden(error)) {
      return <Error403 {...errorPageProps} />;
    }
    if (isNotFound(error)) {
      return <Error404 {...errorPageProps} />;
    }
    return <ErrorCrashedApp isSalesforce={isSalesforce} {...errorPageProps} />;
  };

  const handleError = (error: FallbackError) => {
    monitoring.trackException(error);
    if (isUnauthorized(error)) {
      logout();
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={fallbackComponent}
      onError={handleError}
      resetKeys={[location]}
    >
      {children}
    </ErrorBoundary>
  );
};
